import { IconButton, TableCell } from "@mui/material";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";

import { useGigs } from "../../api/gigs";
import formatDateOnlyString from "../../utils/formatDateString";
import Loader from "../Loader";
import Table from "../Table";
import TableCard from "../TableCard";
import GigStatusCell from "./GigStatusCell";
import { useAppPaths } from "../../Routes";
import FilteredLinkCell from "../FilteredLinkCell";
import { useOrganizationConfig } from "../../api/organization";
import formatDateTimeString from "../../utils/formatDateTimeString";
import { TableHeaderConfig } from "../Table/Table";
import TableChipCell from "../Table/TableChipCell";

export type GigsTableProps = {
    searchTerm?: string,
    gigStatusIds: number[],
};

const GigsTable = ({
    searchTerm,
    gigStatusIds = [],
}: GigsTableProps) => {
    const appPaths = useAppPaths();
    const gigs = useGigs();
    const { giggedClientTerminology, talentTerminology } = useOrganizationConfig();

    const handleOrderByChange = (property: string) => {
        const isAsc = gigs.orderBy === property && gigs.sortOrder === "asc";
        gigs.setSortOrder(isAsc ? "desc" : "asc");
        gigs.setOrderBy(property);
    };

    useEffect(() => {
        gigs.setSearchTerm(searchTerm);
    }, [gigs.setSearchTerm, searchTerm]);

    useEffect(() => {
        gigs.setGigStatusIds(gigStatusIds);
    }, [gigs.setGigStatusIds, gigStatusIds]);

    const headers: TableHeaderConfig[] = [
        {
            label: "Ref no.",
            isSortable: false,
        },
        {
            key: "Title",
            label: "Client name",
            isSortable: true
        },
        {
            label: "Client location",
            isSortable: false
        },
        {
            key: "GiggedClientName",
            label: giggedClientTerminology,
            isSortable: true,
        },
        {
            key: "Industry",
            label: "Industry",
            isSortable: true,
        },
        {
            key: "HasClientHistory",
            label: "Client history?",
            isSortable: true,
        },
        {
            label: `Booked ${talentTerminology.toLowerCase()}`,
            isSortable: false,
        },
        {
            label: "Is confirmed?",
            isSortable: false,
        },
        {
            key: "CreatedAt",
            label: "Date created",
            isSortable: true,
        },
        {
            label: "Created by",
            isSortable: false,
        },
        {
            key: "LastUpdatedAt",
            label: "Last updated",
            isSortable: true,
        },
        {
            key: "GigStatusId",
            label: "Status",
            isSortable: true,
        },
        {
            key: "ExpectedNumberParticipants",
            label: "Expected participants",
            isSortable: true,
        },
        {
            label: "Invites",
            isSortable: false,
        },
        {
            label: "",
            isSortable: false,
            center: true,
        }
    ];

    if (gigs.isLoading) return (
        <Loader />
    );

    return (
        <TableCard>
            <Table
                headers={headers}
                data={gigs.gigs}
                renderRow={gig => (
                    <>
                        <TableCell sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{gig.referenceNumber}</TableCell>
                        <TableCell sx={{ minWidth: "250px" }}>{gig.title}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {gig.location ? 
                                `${gig.location.locality?.locality || ""}${gig.location.locality?.region ? ` (${gig.location.locality.region})` : ""}, ${gig.location.country || ""}`.trim() : 
                                "-"
                            }
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}> {gig.giggedClientName} </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{gig.industry?.name || "-"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{gig.hasClientHistory ? "Yes" : "No"}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{gig.acceptedTalentName || "-"}</TableCell>
                        <TableChipCell labels={[gig.isConfirmed ? "Confirmed" : ""]} />
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateOnlyString(gig.createdAt)}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{gig.createdBy?.name}</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{gig.lastUpdatedAt ? formatDateTimeString(gig.lastUpdatedAt) : "-"}</TableCell>
                        <GigStatusCell gigStatusId={gig.gigStatusId} />
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{gig.expectedNumberParticipants}</TableCell>
                        <FilteredLinkCell
                            count={gig.invitedTalentIds ? gig.invitedTalentIds.length : 0}
                            href={appPaths.gigs.invites(gig.id)}
                        />
                        <TableCell sx={{ textAlign: "right" }}>
                            <Link to={appPaths.gigs.view(gig.id)}>
                                <IconButton aria-label="view" >
                                    <ModeEditOutlineOutlinedIcon color="secondary" />
                                </IconButton>
                            </Link>
                        </TableCell>
                    </>
                )}
                onOrderByChange={handleOrderByChange}
                setPageIndex={gigs.setPageIndex}
                setPageSize={gigs.setPageSize}
                orderBy={gigs.orderBy}
                sortOrder={gigs.sortOrder}
                pageSize={gigs.pageSize}
                pageIndex={gigs.pageIndex}
                totalCount={gigs.totalCount}
                isLoading={gigs.isLoading}
            />
        </TableCard>
    );
};

export default GigsTable;